import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import './CardNav.css';

const ArrowUpRight = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M7.05 16.95a1 1 0 0 1 0-1.41L13.59 9H9a1 1 0 0 1 0-2h7a1 1 0 0 1 1 1v7a1 1 0 1 1-2 0v-4.59l-6.54 6.54a1 1 0 0 1-1.41 0Z" />
  </svg>
);

const CardNav = ({
  logo,
  logoAlt = 'Logo',
  items,
  className = '',
  ease = 'power3.out',
  baseColor = '#fff',
  menuColor,
  buttonBgColor,
  buttonTextColor,
  onItemLinkClick,
  onCtaClick,
  resetKey,
}) => {
  const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef(null);
  const navRef = useRef(null);
  const cardsRef = useRef([]);
  const idRef = useRef('cardnav-' + Math.random().toString(36).slice(2));
  const firstLinkRef = useRef(null);
  const hamburgerButtonRef = useRef(null);

  const calculateHeight = () => {
    const navEl = navRef.current;
    if (!navEl) return 260;

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (isMobile) {
      const contentEl = navEl.querySelector('.card-nav-content');
      if (contentEl) {
        const wasVisible = contentEl.style.visibility;
        const wasPointerEvents = contentEl.style.pointerEvents;
        const wasPosition = contentEl.style.position;
        const wasHeight = contentEl.style.height;

        contentEl.style.visibility = 'visible';
        contentEl.style.pointerEvents = 'auto';
        contentEl.style.position = 'static';
        contentEl.style.height = 'auto';

        // force reflow
        // eslint-disable-next-line no-unused-expressions
        contentEl.offsetHeight;

        const topBar = 60;
        const padding = 16;
        const contentHeight = contentEl.scrollHeight;

        contentEl.style.visibility = wasVisible;
        contentEl.style.pointerEvents = wasPointerEvents;
        contentEl.style.position = wasPosition;
        contentEl.style.height = wasHeight;

        return topBar + contentHeight + padding;
      }
    }
    return 260;
  };

  useLayoutEffect(() => {
    // Initialize to collapsed state on mount or when items change
    if (navRef.current) gsap.set(navRef.current, { height: 60, overflow: 'hidden' });
  }, [items]);

  useLayoutEffect(() => {
    const handleResize = () => {
      // No-op: top bar remains fixed height; content overlays
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isExpanded]);

  // Dynamic viewport height CSS var for mobile (accounts for browser chrome/toolbars)
  useEffect(() => {
    const setVH = () => {
      try {
        const h = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
        // Store in px; consume via calc(var(--dvh) * 0.75 - 60px)
        document.documentElement.style.setProperty('--dvh', `${h}px`);
      } catch (_e) {}
    };
    setVH();
    window.addEventListener('resize', setVH);
    window.visualViewport && window.visualViewport.addEventListener('resize', setVH);
    return () => {
      window.removeEventListener('resize', setVH);
      window.visualViewport && window.visualViewport.removeEventListener('resize', setVH);
    };
  }, []);


  // When resetKey changes (e.g., modal opened/closed), force-collapse
  useLayoutEffect(() => {
    // Ensure collapsed state
    setIsHamburgerOpen(false);
    setIsExpanded(false);
    if (navRef.current) {
      gsap.set(navRef.current, { height: 60, overflow: 'hidden' });
    }
  }, [resetKey]);

  // Escape to close, and outside-click to close while expanded (no page scroll-lock)
  useEffect(() => {
    if (!isExpanded) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
      }
    };
    const onMouseDown = (e) => {
      const el = containerRef.current;
      if (el && !el.contains(e.target)) {
        closeMenu();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [isExpanded]);


  const toggleMenu = () => {
    if (!isExpanded) {
      setIsHamburgerOpen(true);
      setIsExpanded(true);
      if (cardsRef.current?.length) {
        const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
        if (isMobile) {
          // Fade only on mobile to avoid transform stacking/overlap issues
          gsap.fromTo(cardsRef.current, { opacity: 0 }, { opacity: 1, duration: 0.3, ease, stagger: 0.06 });
        } else {
          gsap.fromTo(cardsRef.current, { y: 50, opacity: 0 }, { y: 0, opacity: 1, duration: 0.4, ease, stagger: 0.08 });
        }
      }
      // Focus overlay container instead of the first button to avoid visual focus ring on a single button
      setTimeout(() => {
        const cont = document.getElementById(`${idRef.current}-content`);
        if (cont && typeof cont.focus === 'function') {
          try { cont.focus({ preventScroll: true }); } catch (_e) { cont.focus(); }
        }
      }, 0);
    } else {
      setIsHamburgerOpen(false);
      setIsExpanded(false);
    }
  };

  const setCardRef = (i) => (el) => {
    if (el) cardsRef.current[i] = el;
  };

  const closeMenu = () => {
    // If focus is inside the overlay, move it back to the hamburger before hiding
    const overlayEl = document.getElementById(`${idRef.current}-content`);
    const activeEl = document.activeElement;
    if (overlayEl && activeEl && overlayEl.contains(activeEl)) {
      if (hamburgerButtonRef.current && typeof hamburgerButtonRef.current.focus === 'function') {
        try { hamburgerButtonRef.current.focus({ preventScroll: true }); } catch (_e) { hamburgerButtonRef.current.focus(); }
      } else {
        try { activeEl.blur(); } catch (_e) {}
      }
    }
    setIsHamburgerOpen(false);
    setIsExpanded(false);
    if (navRef.current) {
      gsap.set(navRef.current, { height: 60, overflow: 'hidden' });
    }
  };

  const hardResetMenu = () => {
    closeMenu();
  };

  return (
    <div ref={containerRef} className={`card-nav-container ${className}`}>
      <nav
        ref={navRef}
        className={`card-nav`}
        style={{ backgroundColor: baseColor }}
      >
        <div className="card-nav-top">
          <div className="logo-container">
            {logo ? <img src={logo} alt={logoAlt} className="logo" /> : null}
          </div>

          <button
            type="button"
            className="card-nav-cta-button"
            style={{ backgroundColor: buttonBgColor, color: buttonTextColor }}
            onClick={() => {
              if (typeof onCtaClick === 'function') {
                onCtaClick();
              }
            }}
          >
            Book Your Event
          </button>
        </div>
      </nav>

      <div
        id={`${idRef.current}-content`}
        className={`card-nav-overlay ${isExpanded ? 'open' : ''}`}
        aria-hidden={!isExpanded}
        tabIndex={-1}
        inert={!isExpanded ? '' : undefined}
      >
          {(items || []).slice(0, 3).map((item, idx) => (
            <div
              key={`${item.label}-${idx}`}
              className="nav-card"
              ref={setCardRef(idx)}
              style={{ backgroundColor: item.bgColor, color: item.textColor }}
            >
              <div className="nav-card-step">Step {idx + 1}</div>
              <div className="nav-card-label">{item.label}</div>
              {item.label.includes('Request Sweet Justice') && (
                <div className="nav-card-note">Minimum 50 guests within 15 miles of 33409.</div>
              )}
              {item.label.includes('Sign The Waiver') && (
                <div className="nav-card-note">Waiver must be signed at least 30 days before date of the event.</div>
              )}
              {item.label.includes('Book Your Date') && (
                <div className="nav-card-note">Select an available date; we’ll confirm your booking.</div>
              )}
              <div className="nav-card-actions">
                {item.label.includes('Request Sweet Justice') && (
                  <button
                    type="button"
                    className="nav-card-btn btn-request"
                    ref={idx === 0 ? firstLinkRef : undefined}
                    onClick={() => {
                      if (typeof onCtaClick === 'function') onCtaClick();
                      closeMenu();
                    }}
                  >
                    Request Now
                  </button>
                )}
                {item.label.includes('Sign The Waiver') && (
                  <button
                    type="button"
                    className="nav-card-btn btn-waiver"
                    onClick={() => {
                      try { window.dispatchEvent(new Event('sj-open-waiver')); } catch (_e) {}
                      closeMenu();
                    }}
                  >
                    Complete Waiver
                  </button>
                )}
                {item.label.includes('Book Your Date') && (
                  <a
                    className="nav-card-btn btn-calendar"
                    href="#calendar"
                    onClick={(e) => {
                      let handled = false;
                      if (typeof window !== 'undefined' && typeof window.onCalendarClick === 'function') {
                        e.preventDefault();
                        window.onCalendarClick();
                        handled = true;
                      }
                      closeMenu();
                      return handled;
                    }}
                  >
                    Open Calendar
                  </a>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default CardNav;
